import { Theme, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { TranscriptTypes } from '../../constants/enums';
import {
  selectEventAPI,
  selectEventAPIAuth,
} from '../../redux/features/config/config.selector';
import { selectActiveRestaurantCode } from '../../selectors/restaurant';
import { selectUserProfile } from '../../selectors/user';
import { UserDetails } from '../../types';
import Colors from '../../utils/color';
import { sendAsrIncorrectEvent } from '../../utils/asrEvents';
import ConfirmDialog from '../common/ConfirmDialog';

import {
  BlockedReason,
  EntitySpan,
  EntityType,
} from '../../reducers/messagingSlice';

type TranscriptMessageStyleProps = {
  type: TranscriptTypes;
  isBlocked?: boolean;
  blockedReason?: BlockedReason;
};

const getBlockedColor = (blockedReason?: BlockedReason): string => {
  if (blockedReason === BlockedReason.debounce) {
    return Colors.blockedDebounce;
  }
  if (blockedReason === BlockedReason.pendingAsr) {
    return Colors.blockedPendingAsr;
  }
  return Colors.blockedDefault;
};

const getBlockedTooltipText = (blockedReason?: BlockedReason): string => {
  if (blockedReason) {
    return `Blocked: ${blockedReason}`;
  }
  return 'Blocked';
};

const ENTITY_HIGHLIGHT_COLORS: Record<
  EntityType,
  { background: string; color: string }
> = {
  menu_item: { background: '#BBDEFB', color: '#1565C0' },
  quantity: { background: '#C8E6C9', color: '#2E7D32' },
  coupon: { background: '#FFE0B2', color: '#E65100' },
  discount: { background: '#F8BBD0', color: '#AD1457' },
  loyalty: { background: '#E1BEE7', color: '#6A1B9A' },
  size: { background: '#C5CAE9', color: '#283593' },
};

const useStyles = makeStyles<Theme, TranscriptMessageStyleProps>(({}) => ({
  transcriptMessageClass: {
    fontSize: '14px',
    lineHeight: '20px',
    paddingBottom: '4px',
    display: 'flex',
    color: ({ type, isBlocked, blockedReason }) =>
      isBlocked
        ? getBlockedColor(blockedReason)
        : type === TranscriptTypes.AI
        ? '#1E1E1E'
        : Colors.guardsmanRed,
    textDecoration: ({ isBlocked }) => (isBlocked ? 'line-through' : 'none'),
    textDecorationThickness: ({ isBlocked }) => (isBlocked ? '3px' : 'auto'),
    fontWeight: ({ isBlocked }) => (isBlocked ? 'bold' : 'normal'),
  },
  sourceClass: {
    width: '1.75rem',
    fontWeight: 'bold',
    textDecoration: 'inherit',
  },
  contentClass: {
    width: 'calc(100% - 3.75rem)',
    textDecoration: 'inherit',
  },
  wordSpanClass: {
    display: 'inline-block',
    padding: '0 2px',
    borderRadius: '3px',
    textDecoration: 'inherit',
    '&:hover': {
      backgroundColor: '#fff59d',
      cursor: 'pointer',
    },
  },
  langIndicatorClass: {
    width: '2rem',
    textAlign: 'center',
    paddingRight: '4px',
  },
  secondLangIndicatorClass: {
    backgroundColor: '#0797EA',
    color: '#FFFFFF',
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
  },
  secondLangClass: {
    fontStyle: 'italic',
  },
  entityHighlightClass: {
    borderRadius: '3px',
    padding: '0 2px',
    fontWeight: 600,
  },
}));

interface ITranscriptMessage {
  text: string;
  type: TranscriptTypes;
  secondLangText?: string;
  isBlocked?: boolean;
  blockedReason?: BlockedReason;
  entityAnnotations?: EntitySpan[];
}

export const TranscriptMessage = React.memo(
  ({
    text = '•',
    type,
    secondLangText = '',
    isBlocked = false,
    blockedReason,
    entityAnnotations,
  }: ITranscriptMessage) => {
    const eventAPI = useAppSelector(selectEventAPI);
    const eventAPIAuth = useAppSelector(selectEventAPIAuth);
    const restaurantCode = useAppSelector(selectActiveRestaurantCode);
    const userDetails = useAppSelector(selectUserProfile) as UserDetails | null;
    const dispatch = useAppDispatch();
    const effectiveSessionId = useAppSelector(
      (state) =>
        state.taskRouter.taskAssignedSessionId ||
        state.sessionBoundary.sessionId ||
        state.sessionBoundary.endedSessionId ||
        state.messages.currentSessionId
    );

    const {
      transcriptMessageClass,
      sourceClass,
      contentClass,
      langIndicatorClass,
      secondLangClass,
      secondLangIndicatorClass,
      wordSpanClass,
      entityHighlightClass,
    } = useStyles({
      type,
      isBlocked,
      blockedReason,
    });

    const lastClickRef = useRef<{ word: string; time: number } | null>(null);

    const words = useMemo(() => {
      if (!text) {
        return [] as string[];
      }
      return text.split(/\s+/).filter((w) => w.length > 0);
    }, [text]);

    const annotatedSegments = useMemo(() => {
      if (!entityAnnotations?.length || !text) return null;
      const sorted = [...entityAnnotations].sort((a, b) => a.start - b.start);
      const segments: { text: string; entity?: EntitySpan }[] = [];
      let cursor = 0;
      for (const span of sorted) {
        if (
          span.start < cursor ||
          span.end <= span.start ||
          span.end > text.length
        )
          continue;
        if (span.start > cursor) {
          segments.push({ text: text.slice(cursor, span.start) });
        }
        segments.push({
          text: text.slice(span.start, span.end),
          entity: span,
        });
        cursor = span.end;
      }
      if (cursor < text.length) {
        segments.push({ text: text.slice(cursor) });
      }
      return segments;
    }, [text, entityAnnotations]);

    const [confirmDialog, setConfirmDialog] = useState<{
      open: boolean;
      word: string;
    }>({ open: false, word: '' });

    const openConfirmDialog = useCallback(
      (wordClickedRaw: string | undefined) => {
        const wordClicked =
          !wordClickedRaw || wordClickedRaw === '/silence'
            ? 'ASR MISSING'
            : wordClickedRaw;

        const now = Date.now();
        if (
          lastClickRef.current &&
          lastClickRef.current.word === wordClicked &&
          now - lastClickRef.current.time < 500
        ) {
          return;
        }
        lastClickRef.current = { word: wordClicked, time: now };

        setConfirmDialog({ open: true, word: wordClicked });
      },
      []
    );

    const handleConfirm = useCallback(() => {
      const wordClicked = confirmDialog.word;

      sendAsrIncorrectEvent({
        eventAPI,
        eventAPIAuth,
        restaurantCode,
        effectiveSessionId,
        userDetails,
        wordClicked,
        dispatch,
      });

      setConfirmDialog((prev) => ({ ...prev, open: false }));
    }, [
      confirmDialog.word,
      eventAPI,
      eventAPIAuth,
      restaurantCode,
      effectiveSessionId,
      userDetails,
      dispatch,
    ]);

    const handleCloseDialog = useCallback(() => {
      setConfirmDialog((prev) => ({ ...prev, open: false }));
    }, []);

    const handleContentClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        const target = event.target as HTMLElement | null;
        const wordFromTarget = target?.dataset?.word;
        if (!wordFromTarget) {
          return;
        }

        openConfirmDialog(wordFromTarget);
      },
      [openConfirmDialog]
    );

    const messageContent = (
      <div className={transcriptMessageClass}>
        <div className={langIndicatorClass}>
          <div className={secondLangIndicatorClass}>
            {secondLangText ? 'S' : ''}
          </div>
        </div>
        <div className={sourceClass}>{type}:&nbsp;</div>
        <div className={contentClass} onClick={handleContentClick}>
          <>
            {annotatedSegments
              ? annotatedSegments.map((seg, i) => {
                  const segWords = seg.text
                    .split(/\s+/)
                    .filter((w) => w.length > 0);
                  const wordSpans = segWords.map((word, wi) => (
                    <span
                      key={`${word}-${i}-${wi}`}
                      data-word={word}
                      className={wordSpanClass}
                    >
                      {wi > 0 ? ' ' : ''}
                      {word}
                    </span>
                  ));
                  if (seg.entity) {
                    return (
                      <Tooltip
                        key={`entity-${i}`}
                        title={seg.entity.type.replaceAll('_', ' ')}
                        placement="top"
                      >
                        <span
                          className={entityHighlightClass}
                          style={{
                            backgroundColor:
                              ENTITY_HIGHLIGHT_COLORS[seg.entity.type]
                                ?.background ?? '#E0E0E0',
                            color:
                              ENTITY_HIGHLIGHT_COLORS[seg.entity.type]?.color,
                          }}
                        >
                          {wordSpans}
                        </span>
                      </Tooltip>
                    );
                  }
                  return (
                    <React.Fragment key={`gap-${i}`}>
                      {wordSpans}
                    </React.Fragment>
                  );
                })
              : words.length === 0
              ? text
              : words.map((word, index) => (
                  <span
                    key={`${word}-${index}`}
                    data-word={word}
                    className={wordSpanClass}
                  >
                    {index > 0 ? ' ' : ''}
                    {word}
                  </span>
                ))}
            {secondLangText ? (
              <span className={secondLangClass}>
                {`     | ${secondLangText}`}
              </span>
            ) : null}
          </>
        </div>
      </div>
    );

    return (
      <>
        {isBlocked ? (
          <Tooltip title={getBlockedTooltipText(blockedReason)} placement="top">
            {messageContent}
          </Tooltip>
        ) : (
          messageContent
        )}
        <ConfirmDialog
          open={confirmDialog.open}
          content={`Send ASR incorrect event for word: "${
            confirmDialog.word || 'ASR MISSING'
          }"?`}
          onClose={handleCloseDialog}
          onConfirm={handleConfirm}
          closeBtnText="Cancel"
          confirmBtnText="OK"
        />
      </>
    );
  }
);
